import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import queryString from 'query-string';

import ServiceErrorModal from './common/components/service-error-modal/service-error-modal.component';
import {
  registerUrlListner,
  navigateTo,
  removeUserSessionData,
  scrollToTop,
  showServiceError,
  isSingleUpgradeOrShiftingJourney,
  findParamFromUrl,
  isEDLJourney
} from './common/utils/app.utils';
import { trackMOEEvent } from 'common/services/moengage.service';
import { RADIO_OPTIONS } from 'home/view/manage-feasibility/constants';
import { PRODUCT_TYPE, isBulkUpgrade, MOENGAGE_ANALYTICS_EVENT, ORDER_TYPE, isAOIOrder } from 'common/constants/app.constants';
import config from 'config/config';
import featureToggler from 'config/featureToggler';

import AppHeader from 'app-layout/app-header';
import AppFooter from 'app-layout/app-footer';
import AppSidebar from 'app-layout/app-sidebar';
import AppContainer from 'app-layout/app-container';
import './App.scss';
import MainLoader from 'common/components/loader/loader.component';
import { trackGTM, updateProductInfo } from 'app-layout/utils';
import useJourneySelector from 'useJourneySelector';

const RADIO_SINGLE_SITE = RADIO_OPTIONS[0];
const RADIO_MULTI_SITE = RADIO_OPTIONS[1];
const SELECTED_NOTIFICATION_DEFAULT = {
  showOvRejection: false,
  show: false,
  message: '',
  type: '',
  eventHandler: () => {},
  onCancel: () => {}
};
const TOAST_DEFAULT = {
  show: false,
  message: '',
  type: '',
  closeToastTime: '8000',
  eventHandler: null
};

const AppContext = React.createContext(null);

const App = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [steps, showSteps] = useState(false);
  const [selectedRadio, setSelectedRadio] = useState(RADIO_MULTI_SITE);
  const [notification, setNotification] = useState(SELECTED_NOTIFICATION_DEFAULT);
  const [toast, setToast] = useState(TOAST_DEFAULT);
  const [multiSiteFileUploadSuccess, setMultiSiteFileUploadSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jsonData, setJsonData] = useState(null);

  const [userProfileLoading, isAuthJourney, setIsAuthJourney] = useJourneySelector();

  const updateToast = (show, message, type, eventHandler) => {
    setToast((toast) => ({ ...toast, show, message, type, eventHandler }));
  };

  const updateNotification = (show, message, type, eventHandler, showOvRejection, onCancel) => {
    if (typeof show === 'boolean') {
      setNotification({ show, message, type, eventHandler, showOvRejection, onCancel });
    } else {
      toggelNotification();
    }
  };

  const toggelNotification = () => {
    setNotification(SELECTED_NOTIFICATION_DEFAULT);
  };

  const toggelToastPopup = () => {
    setToast(TOAST_DEFAULT);
  };

  const registerUrlListnerFn = (event) => {
    registerUrlListner(event);
  };

  const registerClearCurrentUserSession = (event) => {
    removeUserSessionData(event);
  };

  const renderSideBar = useCallback(() => {
    if (!isAuthJourney) {
      return null;
    }

    return (
      <div className="sidebar_area">
        <AppSidebar isAuthJourney={isAuthJourney} />
      </div>
    );
  }, [isAuthJourney]);

  const handleNLDOrLLUnauthRedirect = () => {
    const getParams = queryString.parse(window?.location?.search);
    const productType = getParams?.productType;
    const isNLD_OR_LL = productType === PRODUCT_TYPE.NLD || productType === PRODUCT_TYPE.LL;
    if (isNLD_OR_LL || isBulkUpgrade()) {
      navigateTo(config.aehdashboardUrl);
    }
  };

  const renderAppContainer = () => {
    return (
      <AppContainer
        isAuthJourney={isAuthJourney}
        setIsAuthJourney={setIsAuthJourney}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        multiSiteFileUploadSuccess={multiSiteFileUploadSuccess}
        setMultiSiteFileUploadSuccess={setMultiSiteFileUploadSuccess}
        selectedRadio={selectedRadio}
        setSelectedRadio={setSelectedRadio}
        toast={toast}
        toastState={toast}
        setToast={updateToast}
        steps={steps}
        showSteps={showSteps}
        notificationState={notification}
        setNotification={updateNotification}
        closeNetworkCoveragePopup={toggelToastPopup}
        toggelNotification={toggelNotification}
        pageData={jsonData}
        featureToggler={featureToggler}
      />
    );
  };

  const renderAuthContainer = () => {
    if (isEDLJourney()) {
      return renderAppContainer();
    }

    if (jsonData) {
      return (
        <div className={`auth_layout_view main-container-auth bg-grey`}>
          <div className="container">
            <div className={`dedicated_row ${isAOIOrder() ? 'aoi_container' : ''}`}>
              {renderSideBar()}
              <div className="dedicated_column">
                <div className={`right-content-section`}>
                  <div className={selectedRadio.id === RADIO_OPTIONS[1].id ? 'multisite-card' : ''}>{renderAppContainer()}</div>
                </div>
                <AppFooter isAuthJourney={isAuthJourney} />
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderUnauthContainer = () => {
    if (jsonData) {
      return (
        <React.Fragment>
          {renderAppContainer()}
          <AppFooter isAuthJourney={isAuthJourney} />
        </React.Fragment>
      );
    }
  };

  const fetchUnauthTemplateJSON = () => {
    setLoading(true);

    fetch(`https://assets.airtel.in/static-assets/b2b-dedicated-internet/json/data.json`)
      .then((res) => res.json())
      .then((data) => {
        setJsonData(data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        showServiceError({
          show: true,
          redirect: true
        });
      });
  };

  useEffect(() => {
    const b2bSideBarElements = document?.querySelector('.b2b-lib-navbar_list');

    document?.addEventListener('click', registerUrlListnerFn, true);
    b2bSideBarElements?.addEventListener('click', registerClearCurrentUserSession, true);

    return () => {
      document?.removeEventListener('click', registerUrlListnerFn, true);
      b2bSideBarElements?.removeEventListener('click', registerClearCurrentUserSession, true);
    };
  }, []);

  useEffect(() => {
    if (!userProfileLoading) {
      // update product type info
      updateProductInfo();
      fetchUnauthTemplateJSON();

      if (isAuthJourney) {
        let selectedRadioBtn = RADIO_MULTI_SITE;

        if (isSingleUpgradeOrShiftingJourney()) {
          selectedRadioBtn = RADIO_SINGLE_SITE;
        }

        setSelectedRadio(selectedRadioBtn);
      } else {
        const isOrderConfirmationUrl = findParamFromUrl('type', false) === ORDER_TYPE.ORDER_CONFIRMATION;

        // set selected radio to single_site in case of DI/VPN products.
        setSelectedRadio(RADIO_SINGLE_SITE);

        if (!isOrderConfirmationUrl) {
          // single site journey not available for NLD/LL products.
          handleNLDOrLLUnauthRedirect();
        }

        scrollToTop();
        trackMOEEvent(MOENGAGE_ANALYTICS_EVENT.UNAUTH_CHECK_FEASIBILITY_PAGE_VISIT);
      }
    }
  }, [userProfileLoading, isAuthJourney]);

  if (userProfileLoading || loading) {
    return (
      <div className="container">
        <MainLoader />
      </div>
    );
  }

  return (
    <React.Fragment>
      <React.Fragment>
        <AppHeader />
        {isAuthJourney ? renderAuthContainer() : renderUnauthContainer()}
      </React.Fragment>
      <ServiceErrorModal></ServiceErrorModal>
    </React.Fragment>
  );
};

export { AppContext };
export default withRouter(App);
